import { z } from "zod";
import { apiRequest } from "@/api/http";

export const developerAppSchema = z.object({
  id: z.string(),
  packageName: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["active", "archived", "suspended"]),
  activeRelease: z
    .object({
      id: z.string(),
      version: z.string(),
      status: z.enum(["draft", "submitted", "in_review", "accepted", "rejected", "published", "suspended"]),
      releaseBundleAssetId: z.string().nullable(),
      bundleSha256: z.string().nullable(),
      bundleSizeBytes: z.number().nullable(),
      reviewedBy: z.string().nullable().optional(),
      reviewNotes: z.string().nullable().optional(),
      createdAt: z.string().nullable(),
      updatedAt: z.string().nullable(),
    })
    .nullable(),
  latestRelease: z
    .object({
      id: z.string(),
      version: z.string(),
      status: z.enum(["draft", "submitted", "in_review", "accepted", "rejected", "published", "suspended"]),
      releaseBundleAssetId: z.string().nullable(),
      bundleSha256: z.string().nullable(),
      bundleSizeBytes: z.number().nullable(),
      reviewedBy: z.string().nullable().optional(),
      reviewNotes: z.string().nullable().optional(),
      createdAt: z.string().nullable(),
      updatedAt: z.string().nullable(),
    })
    .nullable(),
  releaseCount: z.number(),
  listing: z
    .object({
      tagline: z.string().nullable(),
      description: z.string().nullable(),
      tags: z.array(z.string()).nullable(),
      featured: z.boolean(),
      featuredAt: z.string().nullable(),
    })
    .optional(),
  manifestListing: z
    .object({
      tagline: z.string().nullable(),
      description: z.string().nullable(),
      tags: z.array(z.string()).nullable(),
    })
    .optional(),
  iconAssetId: z.string().nullable().optional(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const mediaAssetSchema = z.object({
  id: z.string(),
  role: z.enum(["store_icon", "gallery_screenshot"]),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  sortOrder: z.number().nullable(),
  releaseId: z.string().nullable(),
  createdAt: z.string().nullable(),
});

export type MediaAsset = z.infer<typeof mediaAssetSchema>;
export type ListingOverride = NonNullable<z.infer<typeof developerAppSchema>["listing"]>;

export const developerAppsResponseSchema = z.object({
  apps: z.array(developerAppSchema),
});

export type DeveloperApp = z.infer<typeof developerAppSchema>;

export const developerReleaseSchema = z.object({
  id: z.string(),
  version: z.string(),
  status: z.enum(["draft", "submitted", "in_review", "accepted", "rejected", "published", "suspended"]),
  releaseBundleAssetId: z.string().nullable().optional(),
  bundleSha256: z.string().nullable().optional(),
  bundleSizeBytes: z.number().nullable().optional(),
  reviewedBy: z.string().nullable().optional(),
  reviewNotes: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

export type DeveloperRelease = z.infer<typeof developerReleaseSchema>;

export function listDeveloperApps(): Promise<{ apps: DeveloperApp[] }> {
  return apiRequest("/console/apps", developerAppsResponseSchema);
}

export function listDeveloperReleases(packageName: string): Promise<{ releases: DeveloperRelease[] }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/releases`,
    z.object({ releases: z.array(developerReleaseSchema) }),
  );
}

export function createDeveloperApp(input: {
  packageName: string;
  displayName: string;
  description?: string | null;
}): Promise<{ app: DeveloperApp }> {
  return apiRequest("/console/apps", z.object({ app: developerAppSchema }), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function submitDeveloperRelease(input: {
  packageName: string;
  releaseId: string;
}): Promise<{ release: NonNullable<DeveloperApp["latestRelease"]> }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(input.packageName)}/releases/${encodeURIComponent(input.releaseId)}/submit`,
    z.object({ release: developerAppSchema.shape.latestRelease.unwrap() }),
    { method: "POST" },
  );
}

export function listDeveloperMedia(packageName: string): Promise<{ media: MediaAsset[] }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/media`,
    z.object({ media: z.array(mediaAssetSchema) }),
  );
}

/** Browser URL for one of the org's own media assets (console session auth). */
export function developerMediaUrl(packageName: string, assetId: string): string {
  return `/api/console/apps/${encodeURIComponent(packageName)}/media/${encodeURIComponent(assetId)}`;
}

export function updateDeveloperListing(
  packageName: string,
  input: { tagline?: string | null; description?: string | null; tags?: string[] | null },
): Promise<{ listing: ListingOverride }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/listing`,
    z.object({ listing: developerAppSchema.shape.listing.unwrap() }),
    { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(input) },
  );
}

export async function uploadDeveloperMedia(
  packageName: string,
  role: MediaAsset["role"],
  file: File,
): Promise<{ asset: MediaAsset }> {
  const dataBase64 = await fileToBase64(file);
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/media`,
    z.object({ asset: mediaAssetSchema }),
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role, fileName: file.name, contentType: file.type || "application/octet-stream", dataBase64 }),
    },
  );
}

export function deleteDeveloperMedia(packageName: string, assetId: string): Promise<{ ok: true }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/media/${encodeURIComponent(assetId)}`,
    z.object({ ok: z.literal(true) }),
    { method: "DELETE" },
  );
}

export function reorderDeveloperMedia(packageName: string, order: string[]): Promise<{ media: MediaAsset[] }> {
  return apiRequest(
    `/console/apps/${encodeURIComponent(packageName)}/media/order`,
    z.object({ media: z.array(mediaAssetSchema) }),
    { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ order }) },
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("could not read file"));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}
